import { canLinkTableClasses, makeTableLinkFilter } from './filter';
import { tableLinkingReducer } from './state';
import type { TableLinkingState } from './state';
import { computed, createModel, effect, signal, untracked } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { GridRowSelectionChange } from '@thinkalpha/ui-table-svelte/gridViewProps';
import type { GridRowId } from '@thinkalpha/ui-table/types/row';
import type { TableClass } from '@thinkalpha/ui-types/contracts/workspace';
import { LinkingMailboxModel } from 'src/models/LinkingMailboxModel';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import type { LinkEvent, TableLinkRow } from 'src/services/WorkspaceService/linking';
import type { WidgetId } from 'src/services/WorkspaceService/selfOwnedWidget';

function linkScalar(row: Readonly<Record<string, unknown>>, field: string): string | undefined {
    const value = row[field];
    if (typeof value === 'string') return value;
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    return undefined;
}

export function tableLinkRowsFromSelection(change: GridRowSelectionChange): readonly TableLinkRow[] {
    return change.rows.map(({ row }) => ({
        platform: linkScalar(row, 'Platform'),
        positionKey: linkScalar(row, 'PositionKey'),
        account: linkScalar(row, 'Account'),
        symbol: linkScalar(row, 'Symbol'),
        traderId: linkScalar(row, 'TraderID'),
    }));
}

export interface TableLinkingModelInit {
    channel: ReadonlySignal<string | null>;
    logger: Logger;
    selectedTableKey: ReadonlySignal<string | null>;
    tabId: WidgetId;
    tableClass: ReadonlySignal<TableClass | null>;
    workspaceService: Pick<WorkspaceService, 'publishLinkIntent' | 'subscribeLinkEvents'>;
}

export const TableLinkingModel = createModel((init: TableLinkingModelInit) => {
    const state = signal<TableLinkingState>({ selectedRowIds: new Set(), linkFilter: undefined });
    const dispatch = (action: Parameters<typeof tableLinkingReducer>[1]): void => {
        state.value = tableLinkingReducer(state.value, action);
    };

    const acceptsLinkEvent = (event: LinkEvent): boolean => {
        const channel = init.channel.value;
        const targetClass = init.tableClass.value;
        return (
            channel !== null &&
            targetClass !== null &&
            event.channelId === channel &&
            event.type === 'table-rows-selected' &&
            event.from.kind === 'widget' &&
            event.from.tabId !== init.tabId &&
            event.from.widgetType === 'table' &&
            canLinkTableClasses(event.details.tableClass, targetClass)
        );
    };
    const linkingMailbox = new LinkingMailboxModel({
        workspaceService: init.workspaceService,
        acceptsLinkEvent,
    });
    effect(() => () => linkingMailbox.dispose());

    effect(() => {
        const linkedSelection = linkingMailbox.tableRows.value;
        if (linkedSelection === null) return;
        untracked(() => {
            const targetClass = init.tableClass.value;
            if (targetClass === null) return;
            dispatch({
                type: 'SET_LINK_FILTER',
                filter: makeTableLinkFilter(linkedSelection.value.tableClass, targetClass, linkedSelection.value.rows),
            });
        });
    });

    let previousChannel = init.channel.value;
    let previousTableClass = init.tableClass.value;
    effect(() => {
        const channel = init.channel.value;
        const tableClass = init.tableClass.value;
        untracked(() => {
            if (channel === previousChannel && tableClass === previousTableClass) return;
            previousChannel = channel;
            previousTableClass = tableClass;
            dispatch({ type: 'RESET_LINKING' });
        });
    });

    let previousTableKey = init.selectedTableKey.value;
    effect(() => {
        const tableKey = init.selectedTableKey.value;
        untracked(() => {
            if (tableKey === previousTableKey) return;
            previousTableKey = tableKey;
            dispatch({ type: 'SET_SELECTED_ROW_IDS', selectedRowIds: new Set() });
        });
    });

    return {
        linkFilter: computed(() => state.value.linkFilter),
        selectedRowIds: computed(() => state.value.selectedRowIds),
        resetChannelData(): void {
            dispatch({ type: 'RESET_LINKING' });
        },
        updateRowSelection(change: GridRowSelectionChange): void {
            dispatch({ type: 'SET_SELECTED_ROW_IDS', selectedRowIds: new Set<GridRowId>(change.selectedRowIds) });
            const tableClass = init.tableClass.value;
            if (tableClass === null) return;

            void init.workspaceService
                .publishLinkIntent({
                    type: 'table-rows-selected',
                    fromTabId: init.tabId,
                    details: { tableClass, rows: tableLinkRowsFromSelection(change) },
                })
                .catch((error: unknown) => {
                    init.logger.warn('Failed to publish Table row selection link', {
                        error,
                        tabId: init.tabId,
                        tableClass,
                    });
                });
        },
    };
});

export type TableLinkingModel = InstanceType<typeof TableLinkingModel>;

export { canLinkTableClasses, makeTableLinkFilter } from './filter';
